import Redirect from '@/components/Redirect'
import CampaignCreate from '@/pages/CampaignCreate'
import CampaignDashboard from '@/pages/CampaignDashboard'

import {
  LandingPageTemplate,
  LandingPageTemplateCreate,
  LandingPageTemplateEdit,
} from '@/pages/LandingPageTemplate'

import { SmsTemplate, SmsTemplateCreate, SmsTemplateEdit } from '@/pages/SmsTemplate'

import { PhishCampaignEmailCreate, PhishCampaignEmailDashboard } from '@/pages/PhishCampaignEmail'
import { PhishCampaignSmsCreate, PhishCampaignSmsDashboard } from '@/pages/PhishCampaignSms'

import {
  PhishEmailTemplate,
  PhishEmailTemplateCreate,
  PhishEmailTemplateEdit,
} from '@/pages/PhishEmailTemplate'

import LandingPageMobileTemplateCreate from '@/pages/LandingPageTemplate/LandingPageMobileTemplateCreate'
import RegisteredUserManagement from '@/pages/RegisteredUserManagement'
import ReportPage from '@/pages/PhishReportPage'
import TrainingCourse from '@/pages/TrainingCourse'
import TrainingNotFound from '@/pages/TrainingNotFound'
import TrainingSession from '@/pages/TrainingSession'
import UserGroupDetails from '@/pages/UserGroupDetails'
import UserGroupManagement from '@/pages/UserGroupManagement'
import CourseManagement from '@/pages/CourseManagement'

import DashboardLayout from '@/shared/Layout/DashboardLayout'
import GuestLayout from '@/shared/Layout/GuestLayout'

import React, { Suspense, useEffect } from 'react'
import { connect } from 'react-redux'
import { Route, Routes } from 'react-router-dom'
import MyVideos from '@/pages/MyVideos'
import CourseReportPage from '@/pages/CourseReportPage'
import PersonalDetailsPage from '@/pages/PersonalDetailsPage'
import usePermissions from '@/hooks/usePermissions'
import AccountsManagement from '@/pages/AccountsManagement'
import VerifyDomainPage from '@/pages/VerifyDomainPage'

const LandingPage = React.lazy(() => import('@/pages/LandingPage'))
const HomePage = React.lazy(() => import('@/pages/HomePage'))

const mapStateToProps = ({ auth }) => ({ token: auth.token })

const LandingPageSuspense = (
  <Suspense fallback={<div></div>}>
    <LandingPage />
  </Suspense>
)

const RenderRoute = ({
  children = [],
  suspense,
  suspenseFallback = <div></div>,
  element,
  permission,
  ...props
}) => {
  const hasPermission = usePermissions(permission)

  let routeElement = <Redirect />

  if (hasPermission) {
    routeElement = suspense ? <Suspense fallback={suspenseFallback}>{element}</Suspense> : element
  }

  return (
    <Route element={routeElement} {...props}>
      {children.map(childProps => RenderRoute(childProps))}
    </Route>
  )
}

const routes = [
  {
    key: '1',
    element: <GuestLayout />,
    children: [
      {
        key: '1_1',
        exact: true,
        suspense: true,
        path: '/training_session/:sessionToken',
        element: <TrainingSession />,
      },
      {
        key: '1_2',
        exact: true,
        suspense: true,
        path: '/training_session/:sessionToken/course/:courseRecordDetailId',
        element: <TrainingCourse />,
      },
      {
        key: '1_3',
        exact: true,
        suspense: true,
        path: '/training_session/not_found',
        element: <TrainingNotFound />,
      },
    ],
  },
  {
    key: '2',
    path: '/*',
    element: <Redirect />,
  },
  {
    key: '3',
    element: <DashboardLayout />,
    children: [
      { key: '3_1', suspense: true, path: '/home', element: <HomePage /> },
      {
        key: '3_2',
        suspense: true,
        path: '/phish_campaign_email',
        permission: 'emailCampaign_view',
        element: <PhishCampaignEmailDashboard />,
        allowCompanies: [2],
      },
      {
        key: '3_3',
        suspense: true,
        path: '/phish_campaign_email/:phishCampaignId',
        permission: 'emailCampaign_view',
        element: <PhishCampaignEmailDashboard />,
      },
      {
        key: '3_3_1',
        suspense: true,
        path: '/phish_campaign_email/:phishCampaignId/:childId',
        permission: 'emailCampaign_view',
        element: <PhishCampaignEmailDashboard />,
      },
      {
        key: '3_4',
        suspense: true,
        path: '/phish_campaign_email_create',
        permission: 'emailCampaign_create',
        element: <PhishCampaignEmailCreate />,
      },
      {
        key: '3_5',
        suspense: true,
        path: '/phish_campaign_sms',
        permission: 'smsCampaign_view',
        element: <PhishCampaignSmsDashboard />,
      },
      {
        key: '3_6',
        suspense: true,
        path: '/phish_campaign_sms/:phishCampaignId',
        permission: 'smsCampaign_view',
        element: <PhishCampaignSmsDashboard />,
      },
      {
        key: '3_6_1',
        suspense: true,
        path: '/phish_campaign_sms/:phishCampaignId/:childId',
        permission: 'smsCampaign_view',
        element: <PhishCampaignSmsDashboard />,
      },
      {
        key: '3_7',
        suspense: true,
        path: '/phish_campaign_sms_create',
        permission: 'smsCampaign_create',
        element: <PhishCampaignSmsCreate />,
      },
      {
        key: '3_8',
        suspense: true,
        path: '/phish_template',
        element: <HomePage />,
      },
      {
        key: '3_9',
        suspense: true,
        path: '/phish_template_email/:type',
        element: <PhishEmailTemplate />,
      },
      {
        key: '3_10',
        suspense: true,
        path: '/phish_template_email/:type/create',
        element: <PhishEmailTemplateCreate />,
      },
      {
        key: '3_11',
        suspense: true,
        path: '/phish_template_email/edit',
        element: <PhishEmailTemplateEdit />,
      },
      {
        key: '3_12',
        suspense: true,
        path: '/phish_template_landing/:type',
        element: <LandingPageTemplate />,
      },
      {
        key: '3_13',
        suspense: true,
        path: '/phish_template_landing/:type/create',
        element: <LandingPageTemplateCreate />,
      },
      {
        key: '3_14',
        suspense: true,
        path: '/phish_template_landing/:type/mobile_create',
        element: <LandingPageMobileTemplateCreate />,
      },
      {
        key: '3_15',
        suspense: true,
        path: '/phish_template_landing/edit',
        element: <LandingPageTemplateEdit />,
      },
      {
        key: '3_16',
        suspense: true,
        path: '/phish_template_sms/:type',
        element: <SmsTemplate />,
      },
      {
        key: '3_17',
        suspense: true,
        path: '/phish_template_sms/:type/create',
        element: <SmsTemplateCreate />,
      },
      {
        key: '3_18',
        suspense: true,
        path: '/phish_template_sms/edit',
        element: <SmsTemplateEdit />,
      },
      {
        key: '3_19',
        suspense: true,
        path: '/campaign',
        permission: 'courseTrainingEvent_view',
        element: <CampaignDashboard />,
      },
      {
        key: '3_20',
        suspense: true,
        path: '/campaign_create',
        permission: 'courseTrainingEvent_create',
        element: <CampaignCreate />,
      },
      {
        key: '3_21',
        suspense: true,
        path: '/campaign/:campaignId',
        permission: 'courseTrainingEvent_view',
        element: <CampaignDashboard />,
      },
      {
        key: '3_22',
        suspense: true,
        path: '/phish_report',
        permission: 'phishReport_view',
        element: <ReportPage />,
      },
      {
        key: '3_23',
        suspense: true,
        path: '/user_management',
        permission: 'user_view',
        element: <RegisteredUserManagement />,
      },
      {
        key: '3_24',
        suspense: true,
        path: '/group_management/:userGroupId',
        permission: 'userGroup_view',
        element: <UserGroupDetails />,
      },
      {
        key: '3_25',
        suspense: true,
        path: '/group_management',
        permission: 'userGroup_view',
        element: <UserGroupManagement />,
      },
      {
        key: '3_26',
        suspense: true,
        path: '/settings',
        element: <HomePage />,
      },
      {
        key: '3_27',
        suspense: true,
        path: '/campaign_course_management',
        permission: 'courseVideo_view',
        element: <CourseManagement />,
      },
      {
        key: '3_28',
        suspense: true,
        path: '/my_videos',
        permission: 'courseVideosList_view',
        element: <MyVideos />,
      },
      {
        key: '3_29',
        suspense: true,
        path: '/course_report',
        permission: 'courseReport_view',
        element: <CourseReportPage />,
      },
      {
        key: '3_30',
        suspense: true,
        path: '/personal_details',
        element: <PersonalDetailsPage />,
      },
      {
        key: '3_31',
        suspense: true,
        path: '/manage_accounts',
        element: <AccountsManagement />,
      },
      {
        key: '3_32',
        suspense: true,
        path: '/verify_domain',
        element: <VerifyDomainPage />,
      },
    ],
  },
  {
    key: '4',
    path: '/login',
    element: LandingPageSuspense,
  },
  {
    key: '5',
    path: '/login_verify',
    element: LandingPageSuspense,
  },
  {
    key: '6',
    path: '/forgot_password',
    element: LandingPageSuspense,
  },
  {
    key: '7',
    path: '/forgot_password_verify',
    element: LandingPageSuspense,
  },
  {
    key: '8',
    path: '/reset_password',
    element: LandingPageSuspense,
  },
]

const getPath = (paths, { path, permission, children = [] }) => {
  if (path) {
    paths.push({ path, permission })
  }

  children.forEach(child => {
    getPath(paths, child)
  })
}

const getPaths = () => {
  const paths = []

  routes.forEach(route => {
    getPath(paths, route)
  })

  return paths
}

const AppRoutes = ({ dispatch, token }) => {
  useEffect(() => {
    if (token) {
      dispatch({
        type: 'auth/getAccountData',
        successCallback: () => {
          dispatch({ type: 'account/getPermissions' })
        },
      })
    }
  }, [token, dispatch])

  return <Routes>{routes.map(route => RenderRoute(route))}</Routes>
}

export default connect(mapStateToProps)(AppRoutes)

export { routes, getPaths }
